import conversationMatches from '@/function/conversation/email/conversation.matches';
import conversationOrder from '@/function/conversation/email/conversation.order';
import { cachedLists, conversationApi } from '@/store/api/conversation/email/conversation.api';
import type { TypeAppDispatch, TypeRootState } from '@/store/store';
import type { TypeConversation, TypeConversationMessage } from '@/types/conversation/types.conversation.email';
import type { TypeConversationContact, TypeConversationContactQuery } from '@/types/conversation/types.conversation.email.contact';

const SUMMARY_THROTTLE_MS = 2_000;

const me = (getState: () => TypeRootState): string | null => getState().auth.user?.authUserId ?? null;

export const cachedContactLists = (getState: () => TypeRootState): TypeConversationContactQuery[] =>
    conversationApi.util.selectCachedArgsForQuery(getState(), 'listConversationContacts') as TypeConversationContactQuery[];

let summaryAt = 0;

let summaryTimer: ReturnType<typeof setTimeout> | null = null;

export const refreshConversationSummary = (dispatch: TypeAppDispatch) => {
    if (summaryTimer) return;
    const wait = Math.max(0, summaryAt + SUMMARY_THROTTLE_MS - Date.now());
    summaryTimer = setTimeout(() => {
        summaryTimer = null;
        summaryAt = Date.now();
        dispatch((inner: TypeAppDispatch, getState: () => TypeRootState) => {
            if (!conversationApi.util.selectCachedArgsForQuery(getState(), 'readConversationSummary').length) return;
            inner(conversationApi.endpoints.readConversationSummary.initiate(undefined, { forceRefetch: true })).unsubscribe();
        });
    }, wait);
};

export const writeConversation = (dispatch: TypeAppDispatch, conversation: TypeConversation) => {
    dispatch((inner: TypeAppDispatch, getState: () => TypeRootState) => {
        const viewer = me(getState);
        for (const query of cachedLists(getState)) {
            inner(conversationApi.util.updateQueryData('listConversations', query, (draft) => {
                const fits = conversationMatches(conversation, query, viewer);
                let found = false;
                for (const page of draft.pages) {
                    const index = page.items.findIndex((item) => item.id === conversation.id);
                    if (index < 0) continue;
                    found = true;
                    if (fits === false) page.items.splice(index, 1);
                    else page.items[index] = conversation;
                }
                if (found || fits !== true || !draft.pages.length) return;

                const order = conversationOrder(query.sort);
                const last = draft.pages[draft.pages.length - 1];
                const tail = last.items[last.items.length - 1];
                if (last.page.hasMore && tail && order(conversation, tail) > 0) return;
                const target = draft.pages.find((page) => {
                    const end = page.items[page.items.length - 1];
                    return !end || order(conversation, end) <= 0;
                }) ?? last;
                target.items.push(conversation);
                target.items.sort(order);
            }));
        }
        inner(conversationApi.util.updateQueryData('readConversation', conversation.id, (draft) => { draft.conversation = conversation; }));
    });
    refreshConversationSummary(dispatch);
};

export const dropConversation = (dispatch: TypeAppDispatch, id: string) => {
    dispatch((inner: TypeAppDispatch, getState: () => TypeRootState) => {
        for (const query of cachedLists(getState)) {
            inner(conversationApi.util.updateQueryData('listConversations', query, (draft) => {
                for (const page of draft.pages) page.items = page.items.filter((item) => item.id !== id);
            }));
        }
    });
    refreshConversationSummary(dispatch);
};

const byDate = (a: TypeConversationMessage, b: TypeConversationMessage): number =>
    new Date(a.date).getTime() - new Date(b.date).getTime() || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

export const writeConversationMessage = (dispatch: TypeAppDispatch, message: TypeConversationMessage) => {
    dispatch(conversationApi.util.updateQueryData('readConversation', message.conversationId, (draft) => {
        const index = draft.messages.findIndex((item) => item.id === message.id);
        if (index >= 0) draft.messages[index] = message;
        else {
            draft.messages.push(message);
            draft.messages.sort(byDate);
        }
    }));
};

export const dropConversationMessage = (dispatch: TypeAppDispatch, ref: { id: string; conversationId: string }) => {
    dispatch(conversationApi.util.updateQueryData('readConversation', ref.conversationId, (draft) => {
        draft.messages = draft.messages.filter((item) => item.id !== ref.id);
    }));
};

const contactFits = (contact: TypeConversationContact, query: TypeConversationContactQuery): boolean | null => {
    if (query.search) return null;
    if (query.kind === 'people') return !contact.automated;
    if (query.kind === 'automated') return contact.automated;
    if (query.kind === 'customers') return Boolean(contact.customerId);
    return true;
};

const contactOrder = (sort: TypeConversationContactQuery['sort']) => (a: TypeConversationContact, b: TypeConversationContact): number => {
    if (sort === 'name') return (a.name || a.address).localeCompare(b.name || b.address, 'it');
    return new Date(b.stats.lastAt ?? 0).getTime() - new Date(a.stats.lastAt ?? 0).getTime();
};

export const writeConversationContact = (dispatch: TypeAppDispatch, contact: TypeConversationContact) => {
    dispatch(conversationApi.util.updateQueryData('readConversationContact', contact.id, () => contact));
    dispatch((inner: TypeAppDispatch, getState: () => TypeRootState) => {
        for (const query of cachedContactLists(getState)) {
            inner(conversationApi.util.updateQueryData('listConversationContacts', query, (draft) => {
                const fits = contactFits(contact, query);
                let found = false;
                for (const page of draft.pages) {
                    const index = page.items.findIndex((item) => item.id === contact.id);
                    if (index < 0) continue;
                    found = true;
                    if (fits === false) page.items.splice(index, 1);
                    else page.items[index] = contact;
                }
                if (found || fits !== true || !draft.pages.length) return;
                const first = draft.pages[0];
                if (draft.pages.length === 1 && !first.page.hasMore) {
                    first.items.push(contact);
                    first.items.sort(contactOrder(query.sort));
                }
            }));
        }
    });
    refreshConversationSummary(dispatch);
};

export const dropConversationContact = (dispatch: TypeAppDispatch, id: string) => {
    dispatch((inner: TypeAppDispatch, getState: () => TypeRootState) => {
        for (const query of cachedContactLists(getState)) {
            inner(conversationApi.util.updateQueryData('listConversationContacts', query, (draft) => {
                for (const page of draft.pages) page.items = page.items.filter((item) => item.id !== id);
            }));
        }
    });
    refreshConversationSummary(dispatch);
};
